import { useState, useEffect } from "react";
import { ViewState, ModalState, OwnerFiles } from "../types/owner-edit";
import { useOwner } from "@/context/OwnerContext";

export const useOwnerEditView = (
  walletAddress: string | null,
  address: string | undefined
) => {
  const { isEditView, setIsEditView, isOwnerView, setIsOwnerView } = useOwner();

  const [viewState, setViewState] = useState<ViewState>({
    isOwnerView: isOwnerView,
    isEditView: isEditView,
    showOwnerView: false,
    openCards: {
      info: false,
      social: false,
    },
  });

  const [modalState, setModalState] = useState<ModalState>({
    showAvatarModal: false,
    showBackgroundModal: false,
    showReportModal: false,
  });

  const [files, setFiles] = useState<OwnerFiles>({
    avatar: null,
    avatarNftNumber: null,
    avatarIndex: null,
    background: null,
    backgroundNftNumber: null,
    backgroundIndex: null,
  });

  // Update view state when context changes
  useEffect(() => {
    setViewState(prev => ({
      ...prev,
      isEditView: isEditView,
      isOwnerView: isOwnerView,
    }));
  }, [isEditView, isOwnerView]);

  // Determine if owner view should be shown
  useEffect(() => {
    if (walletAddress && address) {
      setViewState(prev => ({
        ...prev,
        showOwnerView: walletAddress === address,
      }));
    }
  }, [walletAddress, address]);

  const toggleView = () => {
    const newOwnerView = !viewState.isOwnerView;
    setViewState(prev => ({
      ...prev,
      isOwnerView: newOwnerView,
    }));
    setIsOwnerView(newOwnerView);
  };

  const showEdit = () => {
    setIsEditView(true);
  };

  const exitEdit = () => {
    console.log("exitEdit called, setting isEditView to false");
    setIsEditView(false);
  };

  const toggleModal = (modal: keyof ModalState) => {
    setModalState(prev => ({
      ...prev,
      [modal]: !prev[modal],
    }));
  };

  const openModal = (modal: keyof ModalState) => {
    setModalState(prev => ({
      ...prev,
      [modal]: true,
    }));
  };

  const closeModal = (modal: keyof ModalState) => {
    setModalState(prev => ({
      ...prev,
      [modal]: false,
    }));
  };

  const updateFile = (
    type: keyof OwnerFiles,
    file: File | null,
    nftNumber: string | null,
    index?: number
  ) => {
    setFiles(prev => ({
      ...prev,
      [type]: file,
      [type + "NftNumber"]: nftNumber,
      [type + "Index"]: index,
    }));
  };

  const resetFiles = () => {
    setFiles({
      avatar: null,
      avatarNftNumber: null,
      avatarIndex: null,
      background: null,
      backgroundNftNumber: null,
      backgroundIndex: null,
    });
  };

  return {
    viewState,
    modalState,
    files,
    toggleView,
    showEdit,
    exitEdit,
    toggleModal,
    openModal,
    closeModal,
    updateFile,
    resetFiles,
  };
};
